import * as React from 'react';

type UseThemeReturn = {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
};

export const useTheme = (): UseThemeReturn => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const ThemeContext = React.createContext<UseThemeReturn>({
  theme: 'light',
  toggleTheme: () => {},
});

// --background in globals.css, as hex for the browser's theme-color.
const THEME_COLORS = {light: '#ffffff', dark: '#111620'};

const getInitialTheme = () => {
  return document.documentElement.classList.contains('dark') ||
    window.matchMedia('(prefers-color-scheme: dark)').matches ||
    localStorage.getItem('theme') === 'dark'
    ? 'dark'
    : 'light';
};

export const ThemeProvider = ({children}: {children: React.ReactNode}) => {
  const [theme, setTheme] = React.useState<'light' | 'dark'>(getInitialTheme());

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.classList.toggle('light', theme === 'light');
    localStorage.setItem('theme', theme);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', THEME_COLORS[theme]);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{theme, toggleTheme}}>
      {children}
    </ThemeContext.Provider>
  );
};

export default useTheme;
